import {
  getIntegrationLinkedConsultNoteInfo,
  getIntegrationLinkedSessionInfo,
} from '@heidi/integrations/ehr/data';
import type { QuerySessionResponse } from '@heidi/session/contracts';
import { isAxiosError } from 'axios';

interface LinkLookupSuccess {
  status: 'found';
  data: unknown;
}

interface LinkLookupNotFound {
  status: 'not_found';
  data: null;
  http_status: 422;
}

interface LinkLookupError {
  status: 'error';
  message: string;
  http_status?: number;
}

type LinkLookupResult = LinkLookupSuccess | LinkLookupNotFound | LinkLookupError;

const getLinkLookup = async (request: () => Promise<{ data: unknown }>) => {
  try {
    const { data } = await request();
    return { status: 'found', data } satisfies LinkLookupResult;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 422) {
      return {
        status: 'not_found',
        data: null,
        http_status: 422,
      } satisfies LinkLookupResult;
    }

    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'Unknown request error',
      ...(isAxiosError(error) && error.response?.status
        ? { http_status: error.response.status }
        : {}),
    } satisfies LinkLookupResult;
  }
};

export async function getEhrLinkDebugDetails(session: QuerySessionResponse) {
  const consultNoteId = session.latest_consult_note_id ?? null;
  const [sessionLink, consultNoteLink] = await Promise.all([
    getLinkLookup(() =>
      getIntegrationLinkedSessionInfo({ sessionId: session.session_id })
    ),
    consultNoteId
      ? getLinkLookup(() => getIntegrationLinkedConsultNoteInfo({ consultNoteId }))
      : Promise.resolve({
          status: 'not_applicable' as const,
          reason: 'session.latest_consult_note_id is empty',
        }),
  ]);

  return {
    session_context: {
      session_id: session.session_id,
      latest_consult_note_id: consultNoteId,
    },
    modern_links: {
      session_link: sessionLink,
      consult_note_link: consultNoteLink,
      patient_link: {
        source: 'session.patient_profile',
        data: session.patient_profile
          ? {
              patient_profile_id: session.patient_profile.id ?? null,
              source: session.patient_profile.source,
              ehr_provider: session.patient_profile.ehr ?? null,
              ehr_patient_id: session.patient_profile.ehr_patient_id ?? null,
            }
          : null,
      },
    },
    legacy_session_fields: {
      ehr_appt_id: session.ehr_appt_id ?? null,
      ehr_appt_contact_id: session.ehr_appt_contact_id ?? null,
      ehr_encounter_id: session.ehr_encounter_id ?? null,
      ehr_patient_id: session.ehr_patient_id ?? null,
      ehr_provider: session.ehr_provider ?? null,
    },
  };
}

export type EhrLinkDebugDetails = Awaited<ReturnType<typeof getEhrLinkDebugDetails>>;
